import { useState } from "react";
import { Crown, Zap, Star, Rocket, TrendingUp, Lock, ArrowRight, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import PaymentMethodModal from "@/components/PaymentMethodModal";

export type UpsellTrigger =
  | "limit_reached"
  | "after_quiz"
  | "after_streak"
  | "after_3_questions"
  | "locked_feature"
  | "progress_blocked";

type UpsellConfig = {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  urgency?: string;
  benefits: { icon: React.ReactNode; text: string }[];
  cta: string;
  dismissText: string;
};

const UPSELL_CONFIGS: Record<UpsellTrigger, UpsellConfig> = {
  limit_reached: {
    icon: <Lock className="h-8 w-8 text-white" />,
    title: "Ton crédit journalier est épuisé ! 🔒",
    subtitle: "Tu as utilisé tes 5 messages gratuits. Ne perds pas ton élan d'apprentissage !",
    urgency: "⏰ Reviens demain ou débloque l'accès illimité maintenant",
    benefits: [
      { icon: <Zap className="h-5 w-5 text-primary shrink-0" />, text: "Messages **illimités** chaque jour" },
      { icon: <Star className="h-5 w-5 text-primary shrink-0" />, text: "Photos & exercices **sans restriction**" },
      { icon: <Rocket className="h-5 w-5 text-primary shrink-0" />, text: "Quiz & défis **avancés**" },
      { icon: <TrendingUp className="h-5 w-5 text-primary shrink-0" />, text: "Progression **personnalisée**" },
    ],
    cta: "Débloquer l'accès illimité",
    dismissText: "Revenir demain",
  },
  after_quiz: {
    icon: <Star className="h-8 w-8 text-white" />,
    title: "Bravo pour ce quiz ! 🎯",
    subtitle: "Tu progresses bien. Avec Premium, accède à des exercices avancés et un suivi personnalisé.",
    benefits: [
      { icon: <Zap className="h-5 w-5 text-primary shrink-0" />, text: "Quiz illimités & progressifs" },
      { icon: <TrendingUp className="h-5 w-5 text-primary shrink-0" />, text: "Analyse détaillée de tes performances" },
      { icon: <Rocket className="h-5 w-5 text-primary shrink-0" />, text: "Défis Mode Pro entre camarades" },
    ],
    cta: "Continuer à progresser",
    dismissText: "Plus tard",
  },
  after_streak: {
    icon: <TrendingUp className="h-8 w-8 text-white" />,
    title: "🔥 Quelle série ! Continue comme ça !",
    subtitle: "Ta régularité est impressionnante. Premium te permet d'aller encore plus loin.",
    benefits: [
      { icon: <Zap className="h-5 w-5 text-primary shrink-0" />, text: "Apprentissage **sans limites**" },
      { icon: <Star className="h-5 w-5 text-primary shrink-0" />, text: "Objectifs & niveaux **personnalisés**" },
      { icon: <Rocket className="h-5 w-5 text-primary shrink-0" />, text: "Ne perds pas ta progression !" },
    ],
    cta: "Passer au niveau supérieur",
    dismissText: "Pas maintenant",
  },
  after_3_questions: {
    icon: <Zap className="h-8 w-8 text-white" />,
    title: "Tu apprends vite ! ⚡",
    subtitle: "Il ne te reste que 2 messages gratuits. Passe en Premium pour continuer sans interruption.",
    urgency: "💡 Ne perds pas ton élan — continue à apprendre maintenant",
    benefits: [
      { icon: <Zap className="h-5 w-5 text-primary shrink-0" />, text: "Messages **illimités**" },
      { icon: <Star className="h-5 w-5 text-primary shrink-0" />, text: "Réponses **prioritaires** de l'IA" },
      { icon: <TrendingUp className="h-5 w-5 text-primary shrink-0" />, text: "Suivi de progression **avancé**" },
    ],
    cta: "Continuer sans limites",
    dismissText: "Utiliser mes derniers messages",
  },
  locked_feature: {
    icon: <Lock className="h-8 w-8 text-white" />,
    title: "Fonctionnalité Premium 🏆",
    subtitle: "Cette fonctionnalité est réservée aux membres Premium. Débloque tout le potentiel de KelasAI !",
    benefits: [
      { icon: <Zap className="h-5 w-5 text-primary shrink-0" />, text: "Accès à **toutes** les fonctionnalités" },
      { icon: <Star className="h-5 w-5 text-primary shrink-0" />, text: "Défis, jeux et exercices **avancés**" },
      { icon: <Rocket className="h-5 w-5 text-primary shrink-0" />, text: "Coaching **personnalisé**" },
    ],
    cta: "Débloquer maintenant",
    dismissText: "Retour",
  },
  progress_blocked: {
    icon: <TrendingUp className="h-8 w-8 text-white" />,
    title: "Continue ta progression ! 📈",
    subtitle: "Tu as atteint la limite du plan gratuit. Passe en Premium pour ne pas perdre ton avance.",
    urgency: "🚀 Ne laisse pas ta progression s'arrêter ici",
    benefits: [
      { icon: <Zap className="h-5 w-5 text-primary shrink-0" />, text: "Apprentissage **continu**" },
      { icon: <Star className="h-5 w-5 text-primary shrink-0" />, text: "Plan d'étude **personnalisé**" },
      { icon: <TrendingUp className="h-5 w-5 text-primary shrink-0" />, text: "Tableau de bord **avancé**" },
    ],
    cta: "Débloquer Premium",
    dismissText: "Plus tard",
  },
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: UpsellTrigger;
  remainingMessages?: number;
};

const PremiumUpsellModal = ({ open, onOpenChange, trigger, remainingMessages }: Props) => {
  const [showPayment, setShowPayment] = useState(false);
  const config = UPSELL_CONFIGS[trigger];

  if (showPayment) {
    return <PaymentMethodModal open={showPayment} onOpenChange={(v) => { setShowPayment(v); if (!v) onOpenChange(false); }} />;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl border-0 p-0 overflow-hidden gap-0 [&>button]:hidden">
        {/* Hero header */}
        <div className="relative px-6 pt-8 pb-6 text-center" style={{ background: "var(--gradient-hero)" }}>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
            {config.icon}
          </div>
          <h2 className="text-xl font-black text-white mb-1">{config.title}</h2>
          <p className="text-white/80 text-sm font-medium">{config.subtitle}</p>
          {config.urgency && (
            <div className="mt-3 inline-block px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm">
              <p className="text-white text-xs font-bold">{config.urgency}</p>
            </div>
          )}
          {remainingMessages !== undefined && remainingMessages > 0 && (
            <div className="mt-2">
              <p className="text-white/70 text-xs font-semibold">
                {remainingMessages} message{remainingMessages > 1 ? "s" : ""} restant{remainingMessages > 1 ? "s" : ""}
              </p>
            </div>
          )}
        </div>

        {/* Benefits */}
        <div className="px-6 py-5 space-y-3">
          {config.benefits.map((b, i) => (
            <div key={i} className="flex items-center gap-3 rounded-2xl bg-muted/60 p-3">
              {b.icon}
              <span className="text-sm text-foreground" dangerouslySetInnerHTML={{ __html: b.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            </div>
          ))}

          {/* Price teaser */}
          <div className="text-center pt-2">
            <p className="text-xs text-muted-foreground font-semibold">
              À partir de <span className="text-foreground font-black">3 000 FCFA/mois</span> · Annulable à tout moment
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="px-6 pb-6 flex flex-col gap-2">
          <button
            onClick={() => setShowPayment(true)}
            className="flex items-center justify-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-black text-white transition-all hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
            style={{ background: "var(--gradient-hero)" }}
          >
            <Crown className="h-5 w-5" />
            {config.cta}
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            onClick={() => onOpenChange(false)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors py-2 font-semibold"
          >
            {config.dismissText}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PremiumUpsellModal;
